import styled, { useTheme } from "styled-components";
import Switch from "common/Buttons/Switch";
import Title from "common/Typography/Title";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import AddOrderButton from "features/trades/common/AddOrderButton";
import Button from "common/Buttons/Button";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { useGetTooltipConfigForField } from "features/configuration/GeneralConfiguration/ConfigurationItem/ConfigurationItemTooltipsConfig";
import { typography } from "@shared/common/Themes/typography";
import { OrdersSectionProps } from "@shared/features/tradingTerminal/common/types";
import useOrdersSection from "@shared/features/tradingTerminal/SmartTrade/useOrdersSection";
import Flex from "common/Boxes/Flex";
import { ORDER_TYPE_TO_CREATE_TRADE_TAB } from "@shared/features/tradingTerminal/common/constants";
import DefaultOrder from "features/bots/BotForm/common/DefaultOrder";
import Text from "common/Typography/Text";
import OrdersTable from "features/tradingTerminal/Orders/OrdersTable";
import TrailingSection from "features/tradingTerminal/Trailing/TrailingSection";
import { FormDivider } from "features/tradingTerminal/common/styles";
import ExistingOrderTable from "features/tradingTerminal/Orders/ExistingOrderTable";
import { useEmptyState } from "common/Icons/useEmptyState.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { LanguageNameSpaces, OrderTypes } from "@shared/types";
import { useContext } from "react";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import StopTimeout from "features/tradingTerminal/Orders/StopTimeout";
import { Plus } from "lucide-react";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import { openMessage } from "utils/misc";

const OrdersSection = ({
  orders,
  orderType,
  setOrders,
  useOrders,
  setUseOrders,
  ordersErrors,
  baselineEntryPrice,
  generalConfigurationData,
}: OrdersSectionProps) => {
  const theme = useTheme();
  const { getHelperTooltipProps } = useGetTooltipConfigForField();
  const tooltipProps = getHelperTooltipProps(orderType);
  const { Empty } = useEmptyState();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const {
    generalConfiguration,
    numOrders,
    addOrder,
    removeOrder,
    updateOrder,
    resolveIssues,
    isLoading,
    isFetching,
    ordersDisabled,
    isStop,
    onSwitchClick,
    showEmptyState,
    addOrderButtonDisabledMessage,
    useTradingTerminalOldVersion: useOldVersion,
    ignoreRatios,
  } = useOrdersSection({
    orderType,
    orders,
    setOrders,
    generalConfigurationData,
    baselineEntryPrice,
    ordersErrors,
    useOrders,
    setUseOrders,
  });
  const { isSignal, subGroupIds } = useContext(TradingTerminalContext)!;

  const getAddOrderButton = (size?: number) =>
    theme.isNewLook ? (
      <NewLookAddButton
        type="button"
        aria-label={t("labels.targets")}
        $disabled={!!addOrderButtonDisabledMessage}
        onClick={() =>
          addOrderButtonDisabledMessage
            ? openMessage(addOrderButtonDisabledMessage, "warning")
            : addOrder()
        }
      >
        <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
      </NewLookAddButton>
    ) : (
      <AddOrderButton
        addOrder={addOrder}
        disabledMessage={addOrderButtonDisabledMessage}
        size={size}
      />
    );

  const sectionTitle = (useOldVersion || orderType !== OrderTypes.ENTRY) && (
    <Flex
      $justifyContent={useOldVersion ? "space-between" : "flex-start"}
      style={{ marginBottom: theme.isNewLook ? 12 : 16 }}
    >
      <Title
        level={3}
        style={
          theme.isNewLook ? NEW_LOOK_SECTION_TITLE_STYLE : { display: "flex" }
        }
      >
        {t(ORDER_TYPE_TO_CREATE_TRADE_TAB[orderType])}
        {tooltipProps && isStop && (
          <HelperTooltip
            {...tooltipProps}
            description={tCommon(tooltipProps.description)}
            title={tCommon(tooltipProps.title)}
            iconStyle={{ color: theme.colors.primaryText }}
          />
        )}
      </Title>
      {setUseOrders && (
        <Switch
          checked={useOrders}
          size={useOldVersion || theme.isNewLook ? "small" : "default"}
          onClick={onSwitchClick}
          style={{ marginLeft: 12 }}
        />
      )}
    </Flex>
  );

  const defaultStop = isStop && (
    <div style={{ marginBottom: 20 }}>
      <DefaultOrder
        orderType={OrderTypes.STOP}
        isCreateTrade={true}
        titleStyle={
          theme.isNewLook
            ? {
                fontSize: 12.5,
                lineHeight: "16px",
                fontWeight: theme.typography.fontWeights.MEDIUM,
                color: theme.colors.secondaryText,
              }
            : {
                fontSize: typography.fontSizes.M,
                fontWeight: typography.fontWeights.NORMAL,
              }
        }
        numColumns={1}
      />
    </div>
  );

  // The new look lists the count in "Number of Orders" above and puts the add
  // button in the table header, so this separate row is classic only.
  const numOrdersTitle =
    isStop || theme.isNewLook ? undefined : (
      <Flex style={{ marginBottom: theme.isNewLook ? 8 : 12 }}>
        <TargetsCount>
          {numOrders}{" "}
          {numOrders === 1 ? t("labels.target") : t("labels.targets")}
        </TargetsCount>
        {getAddOrderButton(14)}
      </Flex>
    );

  const ordersAndErrors = (
    <OrdersTable
      orderType={orderType}
      orders={orders}
      useOrders={useOrders}
      ordersErrors={ordersErrors}
      updateOrder={updateOrder}
      removeOrder={removeOrder}
      resolveIssues={resolveIssues}
      baselineEntryPrice={baselineEntryPrice}
      title={numOrdersTitle}
      headerAction={
        theme.isNewLook && !isStop ? getAddOrderButton() : undefined
      }
      spacing={8}
      ignoreRatios={ignoreRatios}
    />
  );

  const trailing = !useOldVersion && (
    <TrailingSection
      orderType={orderType}
      titleStyle={
        theme.isNewLook
          ? {
              fontSize: 14,
              fontWeight: theme.typography.fontWeights.SEMI_BOLD,
              color: theme.colors.titleColor,
            }
          : {
              fontSize: typography.fontSizes.L,
              fontWeight: typography.fontWeights.MEDIUM,
            }
      }
      titleRowStyle={{
        justifyContent: "flex-start",
        margin: theme.isNewLook ? 0 : "8px 0",
      }}
      style={{ marginTop: theme.isNewLook ? 16 : 20 }}
    />
  );

  const setupButton = ordersDisabled && useOldVersion && (
    <SetupButton type="primary" onClick={() => setUseOrders?.(true)}>
      {t(
        orderType === OrderTypes.TAKE_PROFIT
          ? "labels.set_take_profits"
          : "labels.set_stop"
      )}
    </SetupButton>
  );

  const getEmptyStateText = () => {
    if (isSignal) {
      return t(
        subGroupIds?.[0]
          ? "messages.errors.select_exchanges_to_set_targets"
          : "messages.errors.select_group_to_set_targets"
      );
    } else {
      return t("messages.errors.symbol_not_on_accounts");
    }
  };

  const emptyState = (
    <EmptyWrapper>
      <Empty style={{ width: 100, height: 100 }} />
      <Title level={4} style={{ marginTop: 8 }}>
        {t("labels.no_orders")}
      </Title>
      {!useOldVersion && (
        <Text $size={typography.fontSizes.S} type="secondary">
          {getEmptyStateText()}
        </Text>
      )}
    </EmptyWrapper>
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading || isFetching}
      wrap={true}
      spinnerMarginTop={useOldVersion ? undefined : 75}
      hideContentWhileLoading={isLoading}
      wrapStyle={{ height: "auto" }}
      backgroundColor={
        theme.colors[isBottomNavigator ? "layoutBgColor" : "cardBgColor"]
      }
    >
      <Wrapper $disabled={useOldVersion && ordersDisabled}>
        {(useOldVersion || !showEmptyState) && sectionTitle}
        {showEmptyState ? (
          emptyState
        ) : (
          <ContentWrapper
            $disabled={
              !useOldVersion &&
              ordersDisabled &&
              orderType === OrderTypes.TAKE_PROFIT
            }
          >
            <ContentWrapper
              $disabled={!useOldVersion && ordersDisabled && isStop}
            >
              {generalConfiguration}
              {defaultStop}
              {isStop && !isSignal && <StopTimeout />}
              <FormDivider />
              <ExistingOrderTable orderType={orderType} />
              {ordersAndErrors}
            </ContentWrapper>
            {trailing}
          </ContentWrapper>
        )}
        {setupButton}
      </Wrapper>
    </LoadingSpinnerOverlay>
  );
};

export default OrdersSection;

const Wrapper = styled.div<{ $disabled?: boolean }>`
  position: relative;
  height: 100%;
  ${(p) =>
    p.$disabled &&
    `
  overflow: hidden;
  &:after {
    content: "";
    display: block;
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    background-color: ${p.theme.colors.cardBgColor};
    opacity: ${p.$disabled ? (p.theme.isDarkMode ? 0.9 : 0.8) : 0};
  }
  `}
`;
const ContentWrapper = styled.div<{ $disabled?: boolean }>`
  ${(p) =>
    p.$disabled &&
    `
    opacity: ${p.$disabled ? 0.3 : 0};
    pointer-events: none;
    `};
`;
const SetupButton = styled(Button)`
  position: absolute;
  z-index: 1;
  left: 50%;
  top: 50%;
  height: 45px;
  transform: translate(-50%, -50%);
`;
const NEW_LOOK_SECTION_TITLE_STYLE = {
  display: "flex",
  alignItems: "center",
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 600,
};
const TargetsCount = styled(Text)`
  ${(p) =>
    p.theme.isNewLook &&
    `
    font-size: 13px;
    font-weight: ${p.theme.typography.fontWeights.MEDIUM};
    color: ${p.theme.colors.titleColor};
  `}
`;
const NewLookAddButton = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${(p) =>
    p.$disabled ? p.theme.colors.secondaryText : p.theme.colors.primaryBlue};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  cursor: pointer;
  transition: background-color 150ms;
  &:hover {
    background: ${(p) => p.theme.colors.lightBlue};
  }
`;
const EmptyWrapper = styled(Flex)`
  flex-direction: column;
  text-align: center;
  padding: 48px 24px;
`;
